import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Activity Feed Service
 * Aggregates recent activity from all family features into a unified timeline
 */

/**
 * Activity types for different features
 */
export const ACTIVITY_TYPES = {
  ANNOUNCEMENT: 'announcement',
  EVENT: 'event',
  PHOTO: 'photo',
  DISCUSSION: 'discussion',
  HEALTH_UPDATE: 'health_update',
  RECOMMENDATION: 'recommendation',
  RECIPE: 'recipe',
  GOAL: 'goal',
  EMERGENCY_INFO: 'emergency_info',
  FUN_SOCIAL: 'fun_social',
  POST: 'post',
  DOCUMENT: 'document'
};

/**
 * Get icon name for activity type
 */
export const getActivityIcon = (type) => {
  const icons = {
    [ACTIVITY_TYPES.ANNOUNCEMENT]: 'Announcement',
    [ACTIVITY_TYPES.EVENT]: 'Event',
    [ACTIVITY_TYPES.PHOTO]: 'Photo',
    [ACTIVITY_TYPES.DISCUSSION]: 'Forum',
    [ACTIVITY_TYPES.HEALTH_UPDATE]: 'HealthAndSafety',
    [ACTIVITY_TYPES.RECOMMENDATION]: 'Recommend',
    [ACTIVITY_TYPES.RECIPE]: 'Restaurant',
    [ACTIVITY_TYPES.GOAL]: 'Flag',
    [ACTIVITY_TYPES.EMERGENCY_INFO]: 'Warning',
    [ACTIVITY_TYPES.FUN_SOCIAL]: 'Celebration',
    [ACTIVITY_TYPES.POST]: 'Message',
    [ACTIVITY_TYPES.DOCUMENT]: 'Description'
  };
  return icons[type] || 'Info';
};

/**
 * Get color for activity type
 */
export const getActivityColor = (type) => {
  const colors = {
    [ACTIVITY_TYPES.ANNOUNCEMENT]: '#1976d2',
    [ACTIVITY_TYPES.EVENT]: '#f57c00',
    [ACTIVITY_TYPES.PHOTO]: '#7b1fa2',
    [ACTIVITY_TYPES.DISCUSSION]: '#388e3c',
    [ACTIVITY_TYPES.HEALTH_UPDATE]: '#d32f2f',
    [ACTIVITY_TYPES.RECOMMENDATION]: '#0288d1',
    [ACTIVITY_TYPES.RECIPE]: '#f4511e',
    [ACTIVITY_TYPES.GOAL]: '#c62828',
    [ACTIVITY_TYPES.EMERGENCY_INFO]: '#e65100',
    [ACTIVITY_TYPES.FUN_SOCIAL]: '#8e24aa',
    [ACTIVITY_TYPES.POST]: '#00796b',
    [ACTIVITY_TYPES.DOCUMENT]: '#5e35b1'
  };
  return colors[type] || '#757575';
};

/**
 * Get route for activity type
 */
export const getActivityRoute = (type, itemId) => {
  const routes = {
    [ACTIVITY_TYPES.ANNOUNCEMENT]: '/announcements',
    [ACTIVITY_TYPES.EVENT]: '/events',
    [ACTIVITY_TYPES.PHOTO]: '/photos',
    [ACTIVITY_TYPES.DISCUSSION]: '/discussions',
    [ACTIVITY_TYPES.HEALTH_UPDATE]: '/health-updates',
    [ACTIVITY_TYPES.RECOMMENDATION]: '/recommendations',
    [ACTIVITY_TYPES.RECIPE]: '/recipes',
    [ACTIVITY_TYPES.GOAL]: '/goals',
    [ACTIVITY_TYPES.EMERGENCY_INFO]: '/emergency-info',
    [ACTIVITY_TYPES.FUN_SOCIAL]: '/fun-social',
    [ACTIVITY_TYPES.POST]: '/family-board',
    [ACTIVITY_TYPES.DOCUMENT]: '/documents'
  };
  return routes[type] || '/family-board';
};

/**
 * Fetch recent announcements
 */
const fetchAnnouncements = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'announcements'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.ANNOUNCEMENT,
      title: doc.data().title || 'New Announcement',
      description: doc.data().content || doc.data().description,
      author: doc.data().authorName || doc.data().createdBy,
      timestamp: doc.data().createdAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching announcements:', error);
    return [];
  }
};

/**
 * Fetch recent events
 */
const fetchEvents = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'events'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.EVENT,
      title: doc.data().title || doc.data().name || 'New Event',
      description: doc.data().description || `Event on ${doc.data().date?.toDate()?.toLocaleDateString()}`,
      author: doc.data().createdBy,
      timestamp: doc.data().createdAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching events:', error);
    return [];
  }
};

/**
 * Fetch recent photos
 */
const fetchPhotos = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'photos'),
      orderBy('uploadedAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.PHOTO,
      title: 'New Photo',
      description: doc.data().caption || doc.data().description || 'Photo uploaded',
      author: doc.data().uploadedBy,
      timestamp: doc.data().uploadedAt?.toDate() || new Date(),
      imageUrl: doc.data().url || doc.data().photoURL,
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching photos:', error);
    return [];
  }
};

/**
 * Fetch recent discussions
 */
const fetchDiscussions = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'discussions'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.DISCUSSION,
      title: doc.data().title || 'New Discussion',
      description: doc.data().content || doc.data().description,
      author: doc.data().authorName || doc.data().createdBy,
      timestamp: doc.data().createdAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching discussions:', error);
    return [];
  }
};

/**
 * Fetch recent health updates
 */
const fetchHealthUpdates = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'healthUpdates'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.HEALTH_UPDATE,
      title: doc.data().title || 'Health Update',
      description: doc.data().description || doc.data().notes,
      author: doc.data().authorName || doc.data().createdBy,
      timestamp: doc.data().createdAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching health updates:', error);
    return [];
  }
};

/**
 * Fetch recent recommendations
 */
const fetchRecommendations = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'recommendations'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.RECOMMENDATION,
      title: doc.data().title || doc.data().name || 'New Recommendation',
      description: doc.data().description || doc.data().notes,
      author: doc.data().recommendedBy || doc.data().createdBy,
      timestamp: doc.data().createdAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching recommendations:', error);
    return [];
  }
};

/**
 * Fetch recent recipes
 */
const fetchRecipes = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'recipes'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.RECIPE,
      title: doc.data().title || doc.data().name || 'New Recipe',
      description: doc.data().description || `Recipe by ${doc.data().createdBy}`,
      author: doc.data().createdBy,
      timestamp: doc.data().createdAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching recipes:', error);
    return [];
  }
};

/**
 * Fetch recent goals
 */
const fetchGoals = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'goals'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.GOAL,
      title: doc.data().title || doc.data().name || 'New Goal',
      description: doc.data().description,
      author: doc.data().createdBy,
      timestamp: doc.data().createdAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching goals:', error);
    return [];
  }
};

/**
 * Fetch recent posts from family board
 */
const fetchPosts = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'posts'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.POST,
      title: 'New Post',
      description: doc.data().content || doc.data().text,
      author: doc.data().authorName || doc.data().author,
      timestamp: doc.data().timestamp?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching posts:', error);
    return [];
  }
};

/**
 * Fetch recent documents
 */
const fetchDocuments = async (familyCode, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'families', familyCode, 'documents'),
      orderBy('uploadedAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      type: ACTIVITY_TYPES.DOCUMENT,
      title: doc.data().name || 'New Document',
      description: doc.data().description || `Document uploaded`,
      author: doc.data().uploadedBy,
      timestamp: doc.data().uploadedAt?.toDate() || new Date(),
      ...doc.data()
    }));
  } catch (error) {
    logger.error('Error fetching documents:', error);
    return [];
  }
};

/**
 * Get unified activity feed across all family features
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of activities per feature to fetch (default: 5)
 * @returns {Promise<Array>} Sorted array of all activities
 */
export const getActivityFeed = async (familyCode, limitCount = 5) => {
  try {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    // Fetch all activities in parallel
    const [
      announcements,
      events,
      photos,
      discussions,
      healthUpdates,
      recommendations,
      recipes,
      goals,
      posts,
      documents
    ] = await Promise.all([
      fetchAnnouncements(familyCode, limitCount),
      fetchEvents(familyCode, limitCount),
      fetchPhotos(familyCode, limitCount),
      fetchDiscussions(familyCode, limitCount),
      fetchHealthUpdates(familyCode, limitCount),
      fetchRecommendations(familyCode, limitCount),
      fetchRecipes(familyCode, limitCount),
      fetchGoals(familyCode, limitCount),
      fetchPosts(familyCode, limitCount),
      fetchDocuments(familyCode, limitCount)
    ]);

    // Combine all activities
    const allActivities = [
      ...announcements,
      ...events,
      ...photos,
      ...discussions,
      ...healthUpdates,
      ...recommendations,
      ...recipes,
      ...goals,
      ...posts,
      ...documents
    ];

    // Sort by timestamp (most recent first)
    allActivities.sort((a, b) => b.timestamp - a.timestamp);

    // Return top 20 most recent activities
    return allActivities.slice(0, 20);
  } catch (error) {
    logger.error('Error fetching activity feed:', error);
    throw error;
  }
};

/**
 * Get activity feed for a specific feature only
 * @param {string} familyCode - Family code
 * @param {string} activityType - Activity type from ACTIVITY_TYPES
 * @param {number} limitCount - Number of activities to fetch
 * @returns {Promise<Array>} Array of activities for that feature
 */
export const getFeatureActivity = async (familyCode, activityType, limitCount = 10) => {
  try {
    switch (activityType) {
      case ACTIVITY_TYPES.ANNOUNCEMENT:
        return await fetchAnnouncements(familyCode, limitCount);
      case ACTIVITY_TYPES.EVENT:
        return await fetchEvents(familyCode, limitCount);
      case ACTIVITY_TYPES.PHOTO:
        return await fetchPhotos(familyCode, limitCount);
      case ACTIVITY_TYPES.DISCUSSION:
        return await fetchDiscussions(familyCode, limitCount);
      case ACTIVITY_TYPES.HEALTH_UPDATE:
        return await fetchHealthUpdates(familyCode, limitCount);
      case ACTIVITY_TYPES.RECOMMENDATION:
        return await fetchRecommendations(familyCode, limitCount);
      case ACTIVITY_TYPES.RECIPE:
        return await fetchRecipes(familyCode, limitCount);
      case ACTIVITY_TYPES.GOAL:
        return await fetchGoals(familyCode, limitCount);
      case ACTIVITY_TYPES.POST:
        return await fetchPosts(familyCode, limitCount);
      case ACTIVITY_TYPES.DOCUMENT:
        return await fetchDocuments(familyCode, limitCount);
      default:
        return [];
    }
  } catch (error) {
    logger.error(`Error fetching ${activityType} activity:`, error);
    return [];
  }
};
